import { StorageDetailVolume } from '@/components/_SiteWide/TankDisplay/StorageDetailVolume'
import { CaretLeftFilled, CaretRightFilled, LinkOutlined } from '@ant-design/icons'
import { TankFillBar } from '@components/_SiteWide/TankDisplay/TankFillBar'
import { TankProductRow } from '@components/_SiteWide/TankDisplay/TankProductRow'
import { createManifoldAggregate } from '@components/_SiteWide/TankDisplay/util'
import { addCommasToNumber, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { ReadableDifference, ShowRunOutWarningClass, TankHasWarning } from '@utils/general'
import { Button, Col, Row } from 'antd'
import React, { useEffect, useState } from 'react'

export function TankListCards({
  store,
  selectedTank,
  setSelectedTank,
  selectedManifold,
  setSelectedManifold,
  addFunction,
  disabled,
  visibleTankIds,
  setIsVisible,
}) {
  if (!store.tanks) return null

  return (
    <Horizontal className='mb-3' style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: '3rem' }}>
      {store.grouped_tanks.map((group, i) => (
        <TankCardGroup
          key={i}
          group={group}
          count={store.grouped_tanks.length}
          selectedTank={selectedTank}
          setSelectedTank={setSelectedTank}
          selectedManifold={selectedManifold}
          setSelectedManifold={setSelectedManifold}
          addFunction={addFunction}
          disabled={disabled}
          visibleTankIds={visibleTankIds}
          setIsVisible={setIsVisible}
          hideRunout
        />
      ))}
    </Horizontal>
  )
}

function TankCardGroup({
  group,
  count,
  selectedTank,
  setSelectedTank,
  selectedManifold,
  setSelectedManifold,
  addFunction,
  disabled,
  visibleTankIds,
  setIsVisible,
  hideRunout,
}) {
  return (
    <Vertical style={{ overflow: 'unset' }}>
      {(count > 1 || group.type.toLowerCase() === 'carrier') && (
        <Texto className='py-2 ml-3'>{`${group.name.toUpperCase()} ${group.type.toUpperCase()}`}</Texto>
      )}
      <Horizontal alignItems='flex-end' style={{ overflow: 'unset' }} flex={1}>
        {group.tanks
          .filter((tank) => !tank.manifold_id && visibleTankIds?.includes(tank.tank_id))
          .map((tank) => {
            return (
              <Vertical
                key={tank.tank_id}
                flex={1}
                style={{ minWidth: 220, maxWidth: 800 }}
                // height='auto'
                className='bg-1 pb-3 bordered round-border mr-3'
              >
                <TankProductRow
                  key={tank.tank_id}
                  tank={tank}
                  isSelected={tank.tank_id === selectedTank?.tank_id && tank.store_number === selectedTank.store_number}
                  setSelectedTank={(tank) => {
                    setIsVisible(true)
                    setSelectedTank(tank)
                  }}
                  addFunction={addFunction && (() => addFunction(tank))}
                  disabled={disabled}
                  hideRunout={hideRunout}
                />
              </Vertical>
            )
          })}

        {group.manifold_tanks.map((manifold, i) => {
          if (manifold.tanks.some((tank) => visibleTankIds.includes(tank.tank_id))) {
            return (
              <ManifoldCardGroup
                key={i}
                selectedManifold={selectedManifold}
                setSelectedManifold={setSelectedManifold}
                selectedTank={selectedTank}
                manifold={manifold}
                setSelectedTank={setSelectedTank}
                addFunction={addFunction}
                disabled={disabled}
                visibleTankIds={visibleTankIds}
                setIsVisible={setIsVisible}
                hideRunout={hideRunout}
              />
            )
          }
        })}
      </Horizontal>
    </Vertical>
  )
}

function ManifoldCardGroup({
  manifold,
  selectedManifold,
  setSelectedManifold,
  setSelectedTank,
  selectedTank,
  onOrder = false,
  addFunction,
  disabled,
  visibleTankIds,
  setIsVisible,
  hideRunout,
}) {
  const [showingLinkedTanks, setShowingLinkedTanks] = useState(false)
  const [manifoldTank, setManifoldTank] = useState()
  const isSelected = selectedManifold?.manifold_id === manifold.manifold_id
  const projectedVolume = manifold.tanks.some((item) => !!item.projected_volume)
    ? manifold.tanks.map((item) => item.projected_volume || item.current_volume).reduce((prev, next) => prev + next)
    : null
  useEffect(() => {
    setManifoldTank(createManifoldAggregate(manifold))
  }, [manifold.tanks])
  if (!manifoldTank) return null
  return (
    <>
      <Vertical flex={1} className='bg-1 bordered round-border mx-3 pb-3' style={{ minWidth: 220 }}>
        <Horizontal verticalCenter className='bg-theme2 px-3'>
          <Texto
            weight='bold'
            appearance='white'
            style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
          >
            <LinkOutlined className='pr-2' /> {manifold.manifold_id}
          </Texto>
          <Texto appearance='white'>
            <Button
              style={{ color: 'white', background: 'none', height: 28 }}
              type='link'
              onClick={() => setShowingLinkedTanks(!showingLinkedTanks)}
            >
              {showingLinkedTanks ? <CaretLeftFilled /> : <CaretRightFilled />}
              {showingLinkedTanks ? 'Collapse' : 'Expand'}
            </Button>
          </Texto>
        </Horizontal>
        <Horizontal className={`store-details-product-container ${isSelected ? ' selected' : ''}`}>
          <Vertical
            onClick={() => {
              setIsVisible(true)
              setSelectedManifold(manifoldTank)
              setSelectedTank(undefined)
            }}
          >
            <Horizontal verticalCenter style={{ minHeight: 20 }}>
              <Vertical className={`store-detail-product-title ${onOrder ? ' order' : ''}`} flex={4}>
                <Texto
                  className='items-center flex'
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  appearance='secondary'
                >
                  {manifold.tanks[0]?.product}
                </Texto>
              </Vertical>
            </Horizontal>
            {!hideRunout && (
              <Vertical>
                <Vertical flex={1.5} alignItems='flex-start'>
                  <span className={ShowRunOutWarningClass(manifoldTank.run_out_date)}>
                    {ReadableDifference(manifoldTank.run_out_date, undefined, true)}
                  </span>
                </Vertical>
              </Vertical>
            )}
            <Vertical justifyContent='center'>
              <TankFillBar
                fill={manifoldTank.percent_filled}
                showWarning={
                  manifoldTank.run_out_date && !hideRunout ? TankHasWarning(manifoldTank.run_out_date) : false
                }
                projectedFill={projectedVolume / manifoldTank.storage_max}
                lowThreshold={0.5}
              />
            </Vertical>
            <Vertical justifyContent='flex-end'>
              <StorageDetailVolume
                current_volume={addCommasToNumber(manifoldTank.current_volume, 0)}
                projected_volume={projectedVolume && addCommasToNumber(projectedVolume, 0)}
                storage_max={addCommasToNumber(manifoldTank.storage_max, 0)}
              />
            </Vertical>
          </Vertical>
        </Horizontal>
      </Vertical>
      {showingLinkedTanks &&
        manifold.tanks
          .filter((tank) => visibleTankIds.includes(tank.tank_id))
          .map((tank) => (
            <Vertical flex={1} style={{ minWidth: 220, height: '100%' }} className='bg-1 bordered round-border mx-1'>
              <TankProductRow
                key={tank.tank_id}
                tank={tank}
                isSelected={tank.tank_id === selectedTank?.tank_id && tank.store_number === selectedTank.store_number}
                setSelectedTank={(tank) => {
                  setIsVisible(true)
                  selectedManifold && setSelectedManifold(undefined)
                  setSelectedTank(tank)
                }}
                addFunction={addFunction && (() => addFunction(tank))}
                disabled={disabled}
                hideRunout={hideRunout}
              />
            </Vertical>
          ))}
    </>
  )
}
